// The calendar's Month grid, what a chip on it says (#748), and how the Week
// and Day time grids lay a day's blocks out (#749).
//
// The rules are `core/src/week.rs`'s, because both phones draw the same Month
// grid from the same answers: which days the grid holds (whole weeks from the
// Region's first day, four to six rows), what each cell writes (`5`, and
// `Oct 1` on a first), which are outside the month, which is today, a chip's
// title and the one status its glyph shows. `core/src/calendar_grid_recorded.rs`
// replays the recorded answers in `core/src/calendar_grid_vectors.json`.
//
// These are adapters: each shapes its arguments for the boundary and asks.

import { coreSync } from './core.js';

const names = (list) => (Array.isArray(list) ? list.map(String) : []);

// The Month grid's days for the month `monthIso` falls in, from
// `weekStartsOn` (0 Sunday .. 6 Saturday): `{ date, weekday, dayOfMonth,
// label, inMonth, isToday }`. `weekdayNames` are the Region's seven, Sunday
// first, and `monthNames` its twelve, January first; any other count is
// English.
export function monthGrid(monthIso, weekStartsOn, todayIso, weekdayNames, monthNames) {
  return coreSync().monthGrid(String(monthIso), Number(weekStartsOn) || 0, String(todayIso), names(weekdayNames), names(monthNames));
}

// A chip's or a block's title, from its attendees' names in order (`null` for
// one nobody can name): the one student's name, `2 students` for a group as
// MMS writes it by default (#749), or the category label when there is nobody
// on it or its one student cannot be named.
export function chipTitle(attendeeNames, categoryLabel) {
  const list = (attendeeNames || []).map((n) => (n === null || n === undefined ? null : String(n)));
  return coreSync().calendarChipTitle(list, String(categoryLabel ?? ''));
}

// The one status a chip shows, from its students' recorded statuses in
// order: the one student's, and `unrecorded` for a group or an empty lesson.
// A status nobody recorded may arrive as `undefined`, and is none.
export function chipStatus(statuses) {
  const list = Array.isArray(statuses) ? statuses.map((st) => (st ? String(st) : '')) : [];
  return coreSync().calendarStatus(list);
}

// What a screen reader hears beside a group's `2 students`: the attendees who
// can be named, joined by "; ".
export function attendeeNames(attendeeNames) {
  return (attendeeNames || []).filter((n) => n !== null && n !== undefined && n !== '').map(String).join('; ');
}

// One day's timed blocks on the Week or Day grid, laid out by the core's lane
// rule (`day_layout` in core/src/week.rs, #749), the one the phones' Week grid
// is drawn by: `[{ id, startTime, durationMin }]` in, and in the same order
// `[{ startMin, endMin, lane, lanes }]` out — minutes from midnight, a length
// of none or 0 drawn as an hour and a block over midnight cut there, and which
// of `lanes` equal side-by-side columns it takes, 0 the leftmost.
//
// There is no lane rule in JavaScript ("exists once"). MMS lays overlaps out
// by this rule (#749).
export function dayLayout(blocks) {
  const list = (blocks || []).map((b) => ({
    id: String(b.id ?? ''),
    startTime: b.startTime == null ? undefined : String(b.startTime),
    durationMin: typeof b.durationMin === 'number' ? b.durationMin : undefined,
  }));
  return list.length ? coreSync().calendarDayLayout(list) : [];
}

// The hour every time grid opens scrolled to: 7, MMS's default "Initial
// Display Time" (#749). The grid itself is the whole day.
export function gridOpenHour() {
  return coreSync().calendarHours().startHour;
}
